import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import { VotingIndexPage } from './orion/visual/pages/VotingIndexPage'
import { CanvasBoard } from './orion/visual/CanvasBoard'
import { VotingObjectPage } from './orion/visual/pages/VotingObjectPage'
import AgentsListPage from './pages/AgentsListPage'
import { AgentProfilePage } from './pages/AgentProfilePage'
import { AgentConfigPage } from './pages/AgentConfigPage'
import { RAGPage } from './pages/RAGPage'
import { RagLayout } from './components/RAG/RagLayout'
import { KnowledgeBasePage } from './pages/RAG/KnowledgeBasePage'
import { SearchPage } from './pages/RAG/SearchPage'
import { SettingsPage } from './pages/RAG/SettingsPage'
import { CognitionPage } from './pages/RAG/PlaceholderPages'
import { ObservabilityLayout } from './pages/RAG/ObservabilityLayout'
import { GovernanceLayout } from './pages/RAG/GovernanceLayout'
import { ConfigHistoryPage } from './components/RAG/Governance/ConfigHistoryPage'
import { AdaptiveAuditPage } from './components/RAG/Governance/AdaptiveAuditPage'
import { RuntimeStatePage } from './components/RAG/Observability/RuntimeStatePage'
import { HeartbeatPage } from './components/RAG/Observability/HeartbeatPage'
import { DegradationPage } from './components/RAG/Observability/DegradationPage'
import { RAGMasterPage } from './pages/RAGMasterPage'
import DeliberationHistory from './pages/DeliberationHistory'
import DeliberationReplay from './pages/DeliberationReplay'
import DeliberationNew from './pages/DeliberationNew'
import { UserProvider } from './contexts/UserContext'
import { LLMProvider } from './contexts/LLMContext'
import { ToastProvider } from './contexts/ToastContext'
import { SyncProvider, useSync } from './contexts/SyncContext'
import { ProtectedRoute } from './components/ProtectedRoute'
import { SessionConflictModal } from './components/SessionConflictModal'
import { SyncStatusIndicator } from './components/SyncStatusIndicator'
import { OfflineStorage } from './utils/offlineStorage'


function AppContent() {
    const { syncStatus, syncMessage } = useSync()

    // ✅ PHASE 5.3: Periodic cleanup of old partial messages
    useEffect(() => {
        // Cleanup on mount
        OfflineStorage.cleanup().catch(err => {
            console.error('[App] Failed to cleanup partial messages:', err)
        })

        // Cleanup every 6 hours
        const interval = setInterval(() => {
            OfflineStorage.cleanup().catch(err => {
                console.error('[App] Failed to cleanup partial messages:', err)
            })
        }, 6 * 60 * 60 * 1000) // 6 hours

        return () => clearInterval(interval)
    }, [])

    return (
        <>
            <SyncStatusIndicator status={syncStatus} message={syncMessage} />
            <BrowserRouter>
                <SessionConflictModal />
                <Routes>

                    <Route path="/" element={<HomePage />} />
                    <Route path="/orion" element={<ProtectedRoute><CanvasBoard /></ProtectedRoute>} />
                    <Route path="/voting" element={<VotingIndexPage />} />
                    <Route path="/voting/:objectId" element={<VotingObjectPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route
                        path="/agentes"
                        element={
                            <ProtectedRoute>
                                <AgentsListPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/agentes/:handle/configuracao/*"
                        element={
                            <ProtectedRoute>
                                <AgentConfigPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/agentes/:handle"
                        element={
                            <ProtectedRoute>
                                <AgentProfilePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/rag"
                        element={
                            <ProtectedRoute>
                                <RagLayout />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<Navigate to="/rag/knowledge" replace />} />
                        <Route path="knowledge" element={<KnowledgeBasePage />} />
                        <Route path="search" element={<SearchPage />} />
                        <Route path="cognition" element={<CognitionPage />} />
                        <Route path="governance" element={<GovernanceLayout />}>
                            <Route index element={<Navigate to="config-history" replace />} />
                            <Route path="config-history" element={<ConfigHistoryPage />} />
                            <Route path="adaptive-audit" element={<AdaptiveAuditPage />} />
                        </Route>
                        <Route path="observability" element={<ObservabilityLayout />}>
                            <Route index element={<Navigate to="runtime" replace />} />
                            <Route path="runtime" element={<RuntimeStatePage />} />
                            <Route path="heartbeat" element={<HeartbeatPage />} />
                            <Route path="degradation" element={<DegradationPage />} />
                        </Route>
                        <Route path="settings" element={<SettingsPage />} />
                    </Route>
                    <Route
                        path="/rag/:handle"
                        element={
                            <ProtectedRoute>
                                <RAGPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/rag-master"
                        element={
                            <ProtectedRoute>
                                <RAGMasterPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/deliberations"
                        element={
                            <ProtectedRoute>
                                <DeliberationHistory />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/deliberations/:runId"
                        element={
                            <ProtectedRoute>
                                <DeliberationReplay />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/deliberate/new"
                        element={
                            <ProtectedRoute>
                                <DeliberationNew />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </>
    )
}

function App() {
    return (
        <ToastProvider>
            <UserProvider>
                <SyncProvider>
                    <LLMProvider>
                        <AppContent />
                    </LLMProvider>
                </SyncProvider>
            </UserProvider>
        </ToastProvider>
    )
}

export default App
